/**
 * PlacementConfigSection
 *
 * Configures ad placements during bulk deployment.
 * Auto-assigns placements based on available creative aspect ratios.
 * Validates that creatives exist for required placements.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
    Monitor,
    Smartphone,
    Play,
    LayoutGrid,
    Check,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Zap,
    Image
} from 'lucide-react';
import {
    META_PLACEMENTS,
    GOOGLE_PLACEMENTS,
    TIKTOK_PLACEMENTS,
    PLACEMENT_CATEGORIES
} from '../../constants/placements';

// Aspect ratio to placement mapping
const ASPECT_RATIO_PLACEMENTS = {
    '1:1': {
        meta: ['facebook_feed', 'instagram_feed', 'instagram_explore', 'facebook_marketplace'],
        google: ['display', 'discovery'],
        tiktok: []
    },
    '4:5': {
        meta: ['instagram_feed', 'facebook_feed'],
        google: [],
        tiktok: []
    },
    '9:16': {
        meta: ['instagram_stories', 'instagram_reels', 'facebook_stories', 'facebook_reels'],
        google: ['youtube_shorts'],
        tiktok: ['tiktok_feed', 'tiktok_topview']
    },
    '16:9': {
        meta: ['facebook_video_feeds', 'facebook_instream_video', 'facebook_right_column'],
        google: ['youtube_watch', 'youtube_home', 'display'],
        tiktok: []
    },
    '1.91:1': {
        meta: [],
        google: ['display', 'gmail', 'discovery'],
        tiktok: []
    },
    '2:3': {
        meta: [],
        google: [],
        tiktok: []
    }
};

// Category icons
const CATEGORY_ICONS = {
    [PLACEMENT_CATEGORIES.FEED]: LayoutGrid,
    [PLACEMENT_CATEGORIES.STORIES]: Smartphone,
    [PLACEMENT_CATEGORIES.REELS]: Play,
    [PLACEMENT_CATEGORIES.VIDEO]: Play,
    [PLACEMENT_CATEGORIES.DISPLAY]: Monitor,
    [PLACEMENT_CATEGORIES.SEARCH]: Monitor,
    [PLACEMENT_CATEGORIES.SHOPPING]: Monitor,
    [PLACEMENT_CATEGORIES.OTHER]: Monitor
};

/**
 * Get compatible placements based on available aspect ratios
 */
function getCompatiblePlacements(availableRatios, platform) {
    const compatible = new Set();

    for (const ratio of availableRatios) {
        const placements = ASPECT_RATIO_PLACEMENTS[ratio]?.[platform] || [];
        placements.forEach(p => compatible.add(p));
    }

    return Array.from(compatible);
}

/**
 * Get required aspect ratios for selected placements
 */
function getRequiredRatiosForPlacements(placements, platform) {
    const required = new Set();

    for (const placement of placements) {
        for (const [ratio, platformPlacements] of Object.entries(ASPECT_RATIO_PLACEMENTS)) {
            if (platformPlacements[platform]?.includes(placement)) {
                required.add(ratio);
            }
        }
    }

    return Array.from(required);
}

/**
 * Validate creatives exist for placements
 */
function validateCreativesForPlacements(availableRatios, selectedPlacements, platform) {
    const requiredRatios = getRequiredRatiosForPlacements(selectedPlacements, platform);
    const missing = requiredRatios.filter(r => !availableRatios.includes(r));

    if (missing.length > 0) {
        return {
            valid: false,
            missing,
            message: `Missing creatives for aspect ratio${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`
        };
    }

    return { valid: true, missing: [], message: '' };
}

const PlacementConfigSection = ({
    platform = 'meta',
    availableAspectRatios = ['1:1'],
    selectedPlacements = [],
    onPlacementChange,
    autoAssign = true,
    onValidationChange
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [useAutomatic, setUseAutomatic] = useState(true);

    // Get placements for the selected platform
    const platformPlacements = useMemo(() => {
        switch (platform) {
            case 'meta':
                return META_PLACEMENTS;
            case 'google':
                return GOOGLE_PLACEMENTS;
            case 'tiktok':
                return TIKTOK_PLACEMENTS;
            default:
                return META_PLACEMENTS;
        }
    }, [platform]);

    // Get compatible placements based on available aspect ratios
    const compatiblePlacements = useMemo(() =>
        getCompatiblePlacements(availableAspectRatios, platform),
        [availableAspectRatios, platform]
    );

    // Auto-assign placements when in automatic mode or on mount
    useEffect(() => {
        if (autoAssign && useAutomatic) {
            onPlacementChange?.(compatiblePlacements);
        }
    }, [autoAssign, useAutomatic, compatiblePlacements, onPlacementChange]);

    // Validation
    const validation = useMemo(() =>
        validateCreativesForPlacements(availableAspectRatios, selectedPlacements, platform),
        [availableAspectRatios, selectedPlacements, platform]
    );

    // Notify parent of validation changes
    useEffect(() => {
        onValidationChange?.(validation);
    }, [validation, onValidationChange]);

    // Group placements by category
    const placementsByCategory = useMemo(() => {
        const grouped = {};
        for (const [id, placement] of Object.entries(platformPlacements)) {
            const category = placement.category;
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push({ id, ...placement });
        }
        return grouped;
    }, [platformPlacements]);

    // Toggle placement selection
    const togglePlacement = (placementId) => {
        if (useAutomatic) return;

        const newSelection = selectedPlacements.includes(placementId)
            ? selectedPlacements.filter(p => p !== placementId)
            : [...selectedPlacements, placementId];

        onPlacementChange?.(newSelection);
    };

    // Toggle automatic/manual mode
    const handleModeToggle = () => {
        setUseAutomatic(!useAutomatic);
        if (!useAutomatic) {
            // Switching to automatic - use compatible placements
            onPlacementChange?.(compatiblePlacements);
        }
    };

    return (
        <div style={{
            padding: 'var(--space-4)',
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)'
        }}>
            {/* Header */}
            <div
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <LayoutGrid size={20} style={{ color: 'var(--color-primary)' }} />
                    <div>
                        <h4 style={{ margin: 0 }}>Placements</h4>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                            {useAutomatic ? 'Automatic' : 'Manual'} • {selectedPlacements.length} selected
                        </span>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    {!validation.valid && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            color: 'var(--warning-600)',
                            fontSize: 'var(--font-size-sm)'
                        }}>
                            <AlertCircle size={14} />
                            Missing ratios
                        </div>
                    )}
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div style={{ marginTop: 'var(--space-4)' }}>
                    {/* Mode Toggle */}
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-2)',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <button
                            onClick={handleModeToggle}
                            className={useAutomatic ? 'btn btn-primary' : 'btn btn-outline'}
                            style={{ flex: 1 }}
                        >
                            <Zap size={16} />
                            Automatic
                        </button>
                        <button
                            onClick={handleModeToggle}
                            className={!useAutomatic ? 'btn btn-primary' : 'btn btn-outline'}
                            style={{ flex: 1 }}
                        >
                            Manual Selection
                        </button>
                    </div>

                    {/* Available Aspect Ratios */}
                    <div style={{
                        padding: 'var(--space-3)',
                        background: 'var(--color-bg-primary)',
                        borderRadius: 'var(--radius-md)',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-secondary)',
                            marginBottom: 'var(--space-2)'
                        }}>
                            Available Creative Sizes:
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                            {availableAspectRatios.map(ratio => (
                                <span
                                    key={ratio}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-1)',
                                        padding: 'var(--space-1) var(--space-2)',
                                        background: 'var(--success-100)',
                                        color: 'var(--success-700)',
                                        borderRadius: 'var(--radius-sm)',
                                        fontSize: 'var(--font-size-sm)'
                                    }}
                                >
                                    <Image size={12} />
                                    {ratio}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Validation Warning */}
                    {!validation.valid && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-3)',
                            background: 'var(--warning-100)',
                            border: '1px solid var(--warning-300)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <AlertCircle size={20} style={{ color: 'var(--warning-600)' }} />
                            <div>
                                <div style={{ fontWeight: 500, color: 'var(--warning-700)' }}>
                                    Missing Creative Sizes
                                </div>
                                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--warning-600)' }}>
                                    {validation.message}. Generate additional sizes or deselect incompatible placements.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Placement Categories */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        {Object.entries(placementsByCategory).map(([category, placements]) => {
                            const CategoryIcon = CATEGORY_ICONS[category] || Monitor;
                            const selectedInCategory = placements.filter(p => selectedPlacements.includes(p.id)).length;

                            return (
                                <div
                                    key={category}
                                    style={{
                                        padding: 'var(--space-3)',
                                        background: 'var(--color-bg-primary)',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-border)'
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        <CategoryIcon size={16} style={{ color: 'var(--color-text-secondary)' }} />
                                        <span style={{ fontWeight: 500 }}>{category}</span>
                                        <span style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-tertiary)'
                                        }}>
                                            {selectedInCategory}/{placements.length}
                                        </span>
                                    </div>

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                        gap: 'var(--space-2)'
                                    }}>
                                        {placements.map(placement => {
                                            const isSelected = selectedPlacements.includes(placement.id);
                                            const isCompatible = compatiblePlacements.includes(placement.id);

                                            return (
                                                <div
                                                    key={placement.id}
                                                    onClick={() => togglePlacement(placement.id)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 'var(--space-2)',
                                                        padding: 'var(--space-2)',
                                                        background: isSelected
                                                            ? 'var(--color-primary-alpha)'
                                                            : 'var(--color-bg-tertiary)',
                                                        borderRadius: 'var(--radius-sm)',
                                                        border: isSelected
                                                            ? '1px solid var(--color-primary)'
                                                            : '1px solid transparent',
                                                        cursor: useAutomatic ? 'default' : 'pointer',
                                                        opacity: useAutomatic
                                                            ? (isSelected ? 1 : 0.5)
                                                            : (isCompatible ? 1 : 0.5),
                                                        transition: 'all 0.15s ease'
                                                    }}
                                                >
                                                    <div style={{
                                                        width: 16,
                                                        height: 16,
                                                        borderRadius: 'var(--radius-sm)',
                                                        border: isSelected
                                                            ? 'none'
                                                            : '1px solid var(--color-border)',
                                                        background: isSelected
                                                            ? 'var(--color-primary)'
                                                            : 'transparent',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        {isSelected && <Check size={10} style={{ color: 'white' }} />}
                                                    </div>
                                                    <span style={{ fontSize: 'var(--font-size-sm)' }}>
                                                        {placement.icon} {placement.name}
                                                    </span>
                                                    {!isCompatible && (
                                                        <AlertCircle
                                                            size={12}
                                                            style={{
                                                                marginLeft: 'auto',
                                                                color: 'var(--warning-500)'
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlacementConfigSection;

// Export helper functions for use elsewhere
export {
    getCompatiblePlacements,
    getRequiredRatiosForPlacements,
    validateCreativesForPlacements,
    ASPECT_RATIO_PLACEMENTS
};
